import { Chain, IChain, IChannel, IChannelPairDef, SCHEMA_VER, TRecChain, TRecDeviceDef, IDeviceDef, IModuleDef, IAppState } from "./model"
import { ISession, TRecSession, IActiveSession, Session } from "./models/session"
import { TagCache, ITag, Tag, TTagRec } from "./models/tag"
import { nowTimestamp } from "./time"
import { IArch } from "./arch"

const D = console.log

// DEEP COPY
function clone<T>(object:T) : T {
    return JSON.parse(JSON.stringify(object)) as T
}

function isString (value) {
    return typeof value === 'string' || value instanceof String
}

// PP128R legacy FP port range: matrix channels 48..63 stored as positive
// integers. Canonical encoding is negative: -49..-64.
const PP128_LEGACY_FP_MIN = 48
const PP128_LEGACY_FP_MAX = 63

// L/R and channels share refs in memory but become distinct objects after
// JSON round-trip — `Module.create`'s aliasing does not survive serialization.
// All three locations must be migrated to stay consistent.
function migratePP128LegacyFPChain (chain: IChain) : void {
    Chain.nodes(chain).forEach(module => {
        migrateChannelPair(module.L)
        migrateChannelPair(module.R)
        module.channels?.forEach(migrateChannel)
    })
}

function migrateChannelPair (pair?: IChannelPairDef) : void {
    if (!pair) return
    migrateChannel(pair.from)
    migrateChannel(pair.to)
}

function migratePP128LegacyFPDevices (devices: TRecDeviceDef) : void {
    Object.values(devices).forEach(device => {
        Object.values(device.nodes).forEach(module => {
            migrateChannelPair(module.L)
            migrateChannelPair(module.R)
        })
    })
}

function migrateChannel (channel?: IChannel) : void {
    if (!channel) return
    const p = channel.port
    if (p >= PP128_LEGACY_FP_MIN && p <= PP128_LEGACY_FP_MAX) {
        channel.port = -(p + 1)
    }
}

/**
 * ENTITY S11N/DES11N utilities
 */
export class DeSer {
    /**
     * UNPACK chain from its S9D form
     */
    static unpackChain (data:any) : IChain {
        const chain = clone(data)
        chain.lanes.forEach(lane => {
            // SMALL migration for M2S
            // XXX make this a formal migration elsewhere
            const split = !!lane.split
            if ('split' in lane) {
                delete lane.split
            }
            lane.cells.forEach(cell => {
                // REQUIRED `split` prop missing? OLDER schema - migrate!
                if (!('split' in cell)) {
                    cell.split = split
                }
                // ELIDE module instance to merely its ID
                cell.items.forEach(item => {
                    // REQUIRED `split` prop missing? OLDER schema - migrate!
                    if (!('split' in item)) {
                        item.split = split
                    }
                })
            })
        })
        // UPDATE tag schema
        if (chain.state?.tags?.length && isString(chain.state.tags[0])) {
            chain.state.tags = 
                (chain.state.tags as string[]).map(tag => {
                    if (!TagCache.chain[tag]) {
                        TagCache.chain[tag] = Tag.create(tag)
                    }
                    return TagCache.chain[tag]
                })
        }
        const $chain = chain as IChain
        // CACHE tags
        if (chain.state?.tags?.length) {
            $chain.state.tags.forEach(tag => TagCache.chain[tag.name] = tag)
        }
        return $chain
    }

    /**
     * READ application state from packaged S9D form
     * @param data
     * @returns 
     */
    static readAppState (data:any) : IAppState | undefined {
        const app:IAppState = data.app
        // RESET condition
        app.cond = {}
        // Backfill: old sessions lack offlineWarn, default to enabled
        if (app.settings?.offlineWarn === undefined) {
            app.settings = { ...app.settings, offlineWarn: true }
        }
        // MIGRATION: support contact now sourced from URL params (SLI-252)
        delete (app.settings as any).support
        return app
    }

    /**
     * TRANSORM app state to fully packaged S9D form
     */
    static writeAppState (app:IAppState) : any {
        return {
            schema: { version: SCHEMA_VER },
            app,
        }
    }

    /**
     *  PACK chain into S9D form
     */
    static packChain (chain:IChain) : any {
        return clone(chain)
    }

    /**
     * LOAD CHAINS from S9D form
     */
    static readChains (data:any): IChain[] | undefined {
        try {
            const chains = DeSer.unpackChains(data)
            if (chains) {
                return Object.values(chains)
            }
        } catch (e) {
            D('READCHAINS ERR',e)
        }
    }

    /**
     *  PACK chain into S9D form
     */
    static packChains (chains:TRecChain) : Record<string,any> {
        return Object
            .entries(chains)
            .map(([k,v]) => ({k,v:DeSer.packChain(v)}))
            .reduce((p,{k,v}) => {
                p[k] = v
                return p
            },{})
    }

    /**
     * UNPACK chains from S9D form
     */
    static unpackChains (data:any) : Record<string,IChain> | undefined {
        if (data?.chains) {
            return Object
                .entries(data.chains)
                .map(([k,v]) => ({k,v:DeSer.unpackChain(v)}))
                .reduce((p,{k,v}) => {
                    p[k] = v
                    return p
                },{})
        }
    }

    /**
     * TRANSORM chains to fully packaged S9D form
     */
    static writeChains (chains:TRecChain) : any {
        return {
            schema: { version: SCHEMA_VER },
            chains: DeSer.packChains(chains),
        }
    }

    /**
     * TRANSORM active session to fully packaged S9D form
     */
    static writeActive (active: IActiveSession) : any {
        return {
            schema: { version: SCHEMA_VER },
            active,
        }
    }

    /**
     *  PACK devices into S9D form
     */
    static packDevices (devices:TRecDeviceDef) : any {
        return devices
    }

    /**
     * TRANSORM active session to fully packaged S9D form
     */
    static writeDevices (devices:TRecDeviceDef) : any {
        return {
            schema: { version: SCHEMA_VER },
            devices: DeSer.packDevices(devices),
        }
    }

    /**
     * UNPACK device from S9D form
     */
    static unpackDevice(data: any) : IDeviceDef {
        const device = clone(data)
        // UPDATE tag schema
        if (device.nodes) {
            device.nodes =
                (Object.values(device.nodes) as any[])
                .map(node => {
                    // UPDATE tag schema?
                    if (node.tags?.length) {
                        if (isString(node.tags[0])) {
                            node.tags = 
                                (node.tags as string[]).map(tag => {
                                    if (!TagCache.node[tag]) {
                                        TagCache.node[tag] = Tag.create(tag)
                                    }
                                    return TagCache.node[tag]
                                })
                        }
                        const $node = node as IModuleDef
                        // CACHE tags
                        node.tags.forEach(tag => TagCache.node[tag.name] = tag)
                    }
                    return node
                })
                .reduce((p,c) => {
                    p[c.id] = c
                    return p
                },{})
        }

        return device as IDeviceDef
    }

    /**
     * LOAD devices from S9D form
     */
    static readDevices (data:any) : TRecDeviceDef | undefined {
        if (data?.devices) {
            return Object
                .entries(data.devices)
                .reduce((p,[id,device]) => {
                    p[id] = DeSer.unpackDevice(device)
                    return p
                },{})
        }
    }

    /**
     * LOAD active session from S9D form
     */
    static readActive (data: any): IActiveSession | undefined {
        return data?.active
    }

    /**
     * PACKAGE a runtime session for serialization.
     *
     * `arch` is caller-supplied serialization context. Its model is written only
     * to the payload so a later import can decide whether the overlapping 48–63
     * range needs legacy PP128R FP migration; it is not runtime session state.
     */
    static packSession (session:ISession, arch:IArch) : any {
        return {
            ...session,
            // Place after `session` so stale metadata from imported data cannot
            // override the architecture under which this snapshot was saved.
            hw: {model: arch.model.name},
            schema: { version: SCHEMA_VER },
            devices: DeSer.packDevices(session.devices),
            chains: DeSer.packChains(session.chains),
        }
    }

    /**
     * UNPACKAGE a session from serialized form.
     */
    static unpackSession (data:any) : ISession {
        const now = nowTimestamp()
        // `hw` describes the serialized port encoding only. Strip it at this
        // boundary so runtime ISession has no second hardware source of truth.
        const {hw,...sessionData} = data
        const sess = {
            // SUPPLY defaults for some timestamps
            ...{ created: now },
            ...sessionData,
            devices: DeSer.readDevices(data) || {},
            chains: DeSer.readChains(data) || [],
        }

        // SUPPLY defaults for some timestamps
        sess.meta = {
            created: now,
            modified: now,
            accessed: now,
            immutable: false,
            locked: false,
            ...sess.meta
        }

        // New model-aware serializers write hw.model. For backward compatibility,
        // an absent model retains the historical PP128R default, whose positive
        // FP ports need migration before target-arch validation runs.
        const model = hw?.model ?? 'PP128R'
        if (model === 'PP128R') {
            sess.chains.forEach(migratePP128LegacyFPChain)
            if (sess.devices) migratePP128LegacyFPDevices(sess.devices)
        }
        return sess
    }

    /**
     * PACKAGE a collection saved under one current hardware context. Every
     * nested session receives the same serialization metadata as a single save.
     */
    static packSessions (sessions:TRecSession, arch:IArch) : any {
        return {
            schema: { version: SCHEMA_VER },
            sessions:
                Object
                .entries(sessions)
                .reduce((p,[name,session]) => {
                    p[name] = DeSer.packSession(session,arch)
                    return p
                },{})
        }
    }

    /**
     * UNPACK a name-keyed collection of sessions from S8D form.
     * Both stores land here: the local one through readSessions, the remote one
     * with the per-file payloads it fetched. Session ids are repaired here, so
     * a store written before ids were kept unique loads clean.
     */
    static unpackSessions (data:Record<string,any>) : TRecSession {
        return Session.reindex(Object
            .entries(data)
            .reduce((p,[name,session]) => {
                p[name] = DeSer.unpackSession(session)
                return p
            },{} as TRecSession))
    }

    /**
     *  LOAD SESSIONS from S8D FORM
     */
    static readSessions (data:any) : TRecSession | undefined {
        if (data.sessions) {
            return DeSer.unpackSessions(data.sessions)
        }
    }
}
